import { Component, OnInit, inject } from '@angular/core';
import { ImportFlowService } from '@services/imports/import-flow.service';
import { ImportSummaryModel, ImportTriggerResponse } from '@/models/imports/import-summary.model';
import { ImportStatusTableModel } from '@/models/imports/import-status-table-model';
import { ToastrService } from 'ngx-toastr';
import { TableColumnsSetting } from '@/interfaces/table-setting';
import { Subject } from 'rxjs';
import { ImportStatusTableSettings } from '@/table-settings/imports/import-status-table-settings';
import { ImportsStatusDataSource } from '@components/new-table/data-sources/imports-status.dataSource';

@Component({
  selector: 'app-import-summary',
  templateUrl: './import-summary.component.html',
  styleUrls: ['./import-summary.component.scss'],
})
export class ImportSummaryComponent implements OnInit {
  private importFlowService = inject(ImportFlowService);
  private toastr = inject(ToastrService);

  summary: ImportSummaryModel | null = null;
  isLoading = false;
  isTriggeringImport = false;

  // Dati per i grafici
  statusChartData: { label: string; value: number; color: string }[] = [];

  // Tabella registro importazioni
  displayedColumns: TableColumnsSetting<any>;
  dataSource = new ImportsStatusDataSource(this.importFlowService);
  reloadTrigger$ = new Subject<any>();

  ngOnInit(): void {
    this.displayedColumns = ImportStatusTableSettings.getColumnsSetting();
    this.loadSummary();
  }

  loadSummary(): void {
    this.isLoading = true;
    this.loadCounts();
  }

  /**
   * Ricarica solo i counts senza impostare isLoading
   * Usato per aggiornare le statistiche senza distruggere la UI (e quindi la tabella)
   */
  reloadCounts(): void {
    this.importFlowService.getImportRegistryCount().subscribe({
      next: (data) => {
        console.log("Dati count ricevuti dall'API:", data);
        // Mappa i dati dal nuovo formato al modello esistente
        this.summary = {
          totalImports: data.total,
          completed: data.completed,
          inProgress: data.onWorking,
          failed: data.failed,
          pending: data.waiting,
          importsByType: [],
          importsByDate: [],
        };
        this.prepareChartData();
      },
      error: (error) => {
        console.error('Errore nel caricamento del riepilogo:', error);
        this.toastr.error('Errore nel caricamento del riepilogo delle importazioni', 'Errore');
      },
    });
  }

  private loadCounts(): void {
    // Chiama il nuovo endpoint per ottenere i count
    this.importFlowService.getImportRegistryCount().subscribe({
      next: (data) => {
        console.log("Dati count ricevuti dall'API:", data);
        // Mappa i dati dal nuovo formato al modello esistente
        this.summary = {
          totalImports: data.total,
          completed: data.completed,
          inProgress: data.onWorking,
          failed: data.failed,
          pending: data.waiting,
          importsByType: [],
          importsByDate: [],
        };
        this.prepareChartData();
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Errore nel caricamento del riepilogo:', error);
        this.toastr.error('Errore nel caricamento del riepilogo delle importazioni', 'Errore');
        this.summary = this.getEmptySummary();
        this.prepareChartData();
        this.isLoading = false;
      },
    });
  }

  calculateSummaryFromData(imports: any[]): ImportSummaryModel {
    console.log('Elaborazione di', imports.length, 'importazioni');

    if (imports.length === 0) {
      console.warn('Nessuna importazione da elaborare');
      return this.getEmptySummary();
    }

    const totalImports = imports.length;
    let completed = 0;
    let inProgress = 0;
    let failed = 0;
    let pending = 0;

    // Raggruppa per tipo (dal nome del file)
    const importsByTypeMap = new Map<string, number>();
    // Raggruppa per data
    const importsByDateMap = new Map<string, number>();

    let lastImportDate: Date | undefined;

    imports.forEach((item: any, index: number) => {
      if (index === 0) {
        console.log('Esempio di item:', JSON.stringify(item, null, 2));
        console.log('Campi disponibili:', Object.keys(item));
      }

      // Conta per stato - prova diversi nomi di campo possibili
      const state = item.state || item.status || item.importStatus || item.stato;

      // Normalizza lo stato a lowercase per il confronto
      const normalizedState = state ? String(state).toLowerCase().trim() : '';

      if (normalizedState === 'completed' || normalizedState === 'completato') {
        completed++;
      } else if (
        normalizedState === 'on-working' ||
        normalizedState === 'in lavorazione' ||
        normalizedState === 'in_progress' ||
        normalizedState === 'in-progress'
      ) {
        inProgress++;
      } else if (
        normalizedState === 'failed' ||
        normalizedState === 'fallito' ||
        normalizedState === 'error'
      ) {
        failed++;
      } else if (
        normalizedState === 'to-import' ||
        normalizedState === 'da importare' ||
        normalizedState === 'to_import' ||
        normalizedState === 'pending'
      ) {
        pending++;
      } else if (state) {
        // Se c'è uno stato ma non corrisponde a nessuno di quelli noti, loggalo
        console.warn(
          `Stato sconosciuto per item ${index}:`,
          state,
          '(normalized:',
          normalizedState,
          ')',
        );
      }

      // Raggruppa per tipo (estrai tipo dal nome file o usa un campo tipo se disponibile)
      const fileName = item.importFileName || item.fileName || item.nomeFile || '';
      const fileType = this.extractFileType(fileName);
      if (fileType) {
        importsByTypeMap.set(fileType, (importsByTypeMap.get(fileType) || 0) + 1);
      }

      // Raggruppa per data (solo la data, senza l'ora)
      // Prova diversi formati di data
      let createdAt: Date | null = null;
      if (item.createdAt) {
        createdAt = new Date(item.createdAt);
        if (isNaN(createdAt.getTime())) {
          // Se la conversione fallisce, prova altri formati
          console.warn('Data non valida per item:', item.createdAt);
          createdAt = null;
        }
      } else if (item.dataCreazione) {
        createdAt = new Date(item.dataCreazione);
        if (isNaN(createdAt.getTime())) {
          createdAt = null;
        }
      }

      if (createdAt) {
        const dateKey = createdAt.toISOString().split('T')[0]; // YYYY-MM-DD
        importsByDateMap.set(dateKey, (importsByDateMap.get(dateKey) || 0) + 1);

        // Trova l'ultima data di importazione
        if (!lastImportDate || createdAt > lastImportDate) {
          lastImportDate = createdAt;
        }
      }
    });

    console.log('Statistiche calcolate:', {
      totalImports,
      completed,
      inProgress,
      failed,
      pending,
    });

    // Converti le mappe in array per i grafici
    const importsByType = Array.from(importsByTypeMap.entries()).map(([type, count]) => ({
      type,
      count,
    }));

    const importsByDate = Array.from(importsByDateMap.entries())
      .map(([date, count]) => ({
        date: this.formatDateForDisplay(date),
        count,
      }))
      .sort((a, b) => a.date.localeCompare(b.date)); // Ordina per data

    return {
      totalImports,
      completed,
      inProgress,
      failed,
      pending,
      lastImportDate,
      importsByType,
      importsByDate,
    };
  }

  extractFileType(fileName: string): string {
    if (!fileName) return 'Sconosciuto';

    // Estrai il tipo dal nome del file (es. "crossF7_2024.csv" -> "Cross F7")
    const lowerFileName = fileName.toLowerCase();

    if (lowerFileName.includes('crossf7') || lowerFileName.includes('cross-f7')) {
      return 'Cross F7';
    } else if (lowerFileName.includes('crossf8') || lowerFileName.includes('cross-f8')) {
      return 'Cross F8';
    } else if (lowerFileName.includes('loyalty')) {
      return 'Loyalty';
    } else if (lowerFileName.includes('vwfs') && lowerFileName.includes('dashboard')) {
      return 'VWFS Dashboard';
    } else if (lowerFileName.includes('vwfs') && lowerFileName.includes('commission')) {
      return 'VWFS Commission';
    } else if (lowerFileName.includes('vwfs') && lowerFileName.includes('frame')) {
      return 'VWFS Frame';
    } else if (lowerFileName.includes('vwbank')) {
      return 'VW Bank';
    } else if (lowerFileName.includes('vhm')) {
      return 'VHM';
    } else {
      // Estrai l'estensione o usa il nome base
      const parts = fileName.split('.');
      if (parts.length > 1) {
        return parts[0].toUpperCase();
      }
      return 'Altro';
    }
  }

  formatDateForDisplay(dateString: string): string {
    // Converte YYYY-MM-DD in DD/MM/YYYY
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  }

  getEmptySummary(): ImportSummaryModel {
    return {
      totalImports: 0,
      completed: 0,
      inProgress: 0,
      failed: 0,
      pending: 0,
      importsByType: [],
      importsByDate: [],
    };
  }

  prepareChartData(): void {
    if (!this.summary) return;

    // Dati per grafico a barre status
    this.statusChartData = [
      { label: 'Completate', value: this.summary.completed, color: '#4caf50' },
      { label: 'In Lavorazione', value: this.summary.inProgress, color: '#ff9800' },
      { label: 'Fallite', value: this.summary.failed, color: '#f44336' },
      { label: 'In Attesa', value: this.summary.pending, color: '#2196f3' },
    ];
  }

  triggerManualImport(): void {
    if (this.isTriggeringImport) return;

    this.isTriggeringImport = true;
    this.importFlowService.triggerManualImport().subscribe({
      next: (response: any) => {
        // Controlla lo status code: se è 200, è un successo
        if (response.status === 200) {
          // Successo: mostra messaggio di successo
          const message = response.body?.message || 'Import avviato con successo';
          this.toastr.success(message, 'Successo');
          // Ricarica i dati dopo un breve delay
          setTimeout(() => {
            // Ricarica solo i counts senza distruggere la UI
            this.reloadCounts();
            // Ricarica la tabella
            this.reloadTrigger$.next(null);
          }, 2000);
        } else {
          // Status code diverso da 200: mostra messaggio di errore
          const errorMessage = response.body?.message || "Errore durante l'avvio dell'import";
          this.toastr.error(errorMessage, 'Errore');
        }
        // Sblocca sempre il pulsante
        this.isTriggeringImport = false;
      },
      error: (error) => {
        // Gestisce errori HTTP (4xx, 5xx, ecc.)
        console.error("Errore nel lancio dell'import:", error);
        const errorMessage =
          error?.error?.message || error?.message || "Errore durante l'avvio dell'import manuale";
        this.toastr.error(errorMessage, 'Errore');
        // Sblocca sempre il pulsante
        this.isTriggeringImport = false;
      },
    });
  }

  getMaxValue(): number {
    if (!this.statusChartData.length) return 0;
    return Math.max(...this.statusChartData.map((d) => d.value), 1);
  }

  getBarHeight(value: number): number {
    const max = this.getMaxValue();
    if (max === 0) return 0;
    return (value / max) * 100;
  }
}
